import { observer } from "mobx-react-lite";
import { Info } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  CategoryBadge,
  LoadingBar,
  MetricCodeBadge,
  PaginationControls,
} from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatValue } from "../../domains/formatting";
import { dashboardEsgDataTableStore } from "../../stores/esg-data-table.store";
import {
  formatMetricPageLabel,
  getCellStateNote,
  getRowDataNote,
  resolveCellPresentation,
  stripDimKey,
} from "./data-table-cell.helpers";

const RowSkeleton = ({ cols }) => (
  <TableRow className="text-xs">
    {Array.from({ length: cols }).map((_, i) => (
      <TableCell key={i}>
        <div className="h-3 w-full animate-pulse rounded bg-muted/60" />
      </TableCell>
    ))}
  </TableRow>
);

// none/single/multi labels keyed off the selection count; the single case
// falls back to the matched option's name. A null label is dropped from the
// joined summary, which is how a dimension gets omitted entirely.
const dimLabel = (count, match, { none, single, multi }) => {
  if (count === 0) return none;
  if (count === 1) return match?.name ?? single;
  return multi;
};

const DataTableGrid = observer(
  ({
    view,
    years,
    bus,
    countries,
    buOptions,
    countryOptions,
    dimKeys,
    dimLabelFor,
    headerRight,
  }) => {
    const { t } = useTran();
    const store = dashboardEsgDataTableStore;
    const { rows, total, page, pageSize, loading, hasLoadedOnce } = store;

    const labelFor = dimLabelFor;
    const visibleRows = rows;

    const showTargetCol = view === "year";
    const buMatch =
      bus.length === 1
        ? buOptions.find((o) => String(o.id) === String(bus[0]))
        : null;
    const countryMatch =
      countries.length === 1
        ? countryOptions.find((o) => String(o.id) === String(countries[0]))
        : null;
    const yearRange = dimKeys.length
      ? `${dimKeys[0]}–${dimKeys[dimKeys.length - 1]}`
      : t("All years");

    let buLabel;
    let countryLabel;
    let yearLabel;
    if (view === "year") {
      buLabel = dimLabel(bus.length, buMatch, {
        none: t("All Reporting Entities"),
        single: t("Single BU"),
        multi: t("Multi-BU"),
      });
      countryLabel = dimLabel(countries.length, countryMatch, {
        none: t("All Countries"),
        single: t("Single country"),
        multi: t("Multi-country"),
      });
      yearLabel = yearRange;
    } else if (view === "bu") {
      buLabel = t("Multi-BU");
      countryLabel = dimLabel(countries.length, countryMatch, {
        none: null,
        single: t("Single country"),
        multi: t("Multi-country"),
      });
      yearLabel = years[0] ?? t("1 year");
    } else {
      buLabel = dimLabel(bus.length, buMatch, {
        none: null,
        single: t("Single BU"),
        multi: t("Multi-BU"),
      });
      countryLabel = t("Multi-country");
      yearLabel = years[0] ?? t("1 year");
    }
    const summary = [buLabel, countryLabel, yearLabel]
      .filter(Boolean)
      .join(" · ");

    if (view !== "year" && (!years || years.length === 0)) {
      return (
        <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-xs text-muted-foreground">
          {view === "bu"
            ? t("Select a year to view by BU.")
            : t("Select a year to view by Country.")}
        </div>
      );
    }

    if (dimKeys.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-xs text-muted-foreground">
          {view === "year"
            ? t("No years available.")
            : view === "bu"
              ? t("Select at least one business unit.")
              : t("Select at least one country.")}
        </div>
      );
    }

    const colCount = dimKeys.length + (showTargetCol ? 3 : 2);
    const isInitialLoading = loading && !hasLoadedOnce;
    const isRefreshing = loading && hasLoadedOnce;

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">{summary}</p>
          {headerRight}
        </div>
        <div
          className={cn(
            "relative max-h-[475px] overflow-auto rounded-md border border-border/60",
            isRefreshing && "opacity-60",
          )}
        >
          {isRefreshing && <LoadingBar />}
          {/* One provider for the whole table: per-row providers would each
              own their delay state, defeating Radix's skip-delay grouping
              and re-incurring the open delay on every row change. */}
          <TooltipProvider delayDuration={100}>
            <Table isDashboardTable={true}>
              <TableHeader>
                <TableRow className="bg-muted/40 [&_th]:text-[11px] [&_th]:font-medium [&_th]:text-muted-foreground [&_th]:py-2">
                  <TableHead className="w-[200px] sticky top-0 z-50 bg-background">
                    {t("Metrics")}
                  </TableHead>

                  <TableHead className="w-[80px] sticky top-0 z-50 bg-background">
                    {t("Category")}
                  </TableHead>
                  {dimKeys.map((k) => (
                    <TableHead
                      key={k}
                      className={cn(
                        "w-[120px] text-right sticky top-0 z-50 bg-background",
                        view === "year" && "w-[90px]",
                      )}
                      title={labelFor(k)}
                    >
                      {labelFor(k)}
                    </TableHead>
                  ))}
                  {showTargetCol && (
                    <TableHead className="w-[90px] sticky top-0 z-50 bg-background text-right">
                      {t("Target")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              {/* Switched TableBody wrapper behavior into a max-height layout context */}
              <TableBody className="max-h-[500px] overflow-y-auto">
                {isInitialLoading ? (
                  Array.from({ length: pageSize }).map((_, i) => (
                    <RowSkeleton key={i} cols={colCount} />
                  ))
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={colCount}
                      className="text-center text-xs text-muted-foreground py-6"
                    >
                      {hasLoadedOnce
                        ? t("No data for selection.")
                        : t("Loading…")}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => {
                    const note = getRowDataNote({ row, dimKeys, view, t });
                    return (
                      <TableRow key={row.disclosureId} className="text-xs">
                        <TableCell className="min-w-[600px] whitespace-normal">
                          <div className="flex items-start text-xs leading-normal min-w-0 break-words">
                            {/* Badge rendered inline-block so text flows right next to it */}
                            <MetricCodeBadge
                              code={row.code}
                              category={row.category}
                              className="mr-2 shrink-0"
                            />
                            <div className="mt-1">
                              <span title={row.title} className="align-middle">
                                {row.title}
                              </span>
                              {row.unit && (
                                <span className="text-[11px] text-muted-foreground inline-block ml-1.5 mt-0.5 align-middle">
                                  · {row.unit}
                                </span>
                              )}
                              {note && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      aria-label={t(
                                        "Explain the values in this row",
                                      )}
                                      className="ml-1.5 inline-flex cursor-help rounded-sm align-middle text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                      <Info
                                        aria-hidden="true"
                                        className="h-3.5 w-3.5"
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    align="start"
                                    className="max-w-sm"
                                  >
                                    {note}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <CategoryBadge category={row.category} />
                        </TableCell>
                        {dimKeys.map((k) => {
                          const presentation = resolveCellPresentation({
                            row,
                            key: stripDimKey(k, view),
                            view,
                          });
                          const cellNote = getCellStateNote({
                            presentation,
                            t,
                          });
                          return (
                            <TableCell
                              key={k}
                              title={cellNote ?? undefined}
                              className={cn(
                                "text-right",
                                presentation.kind !== "value" &&
                                  "text-muted-foreground",
                              )}
                            >
                              {presentation.kind === "value"
                                ? formatValue(presentation.value)
                                : presentation.text}
                              {/* Screen readers get the explanation as cell
                                content; aria-label on a td is AT-dependent
                                and can replace the visible glyph. */}
                              {cellNote && (
                                <span className="sr-only"> {cellNote}</span>
                              )}
                            </TableCell>
                          );
                        })}
                        {showTargetCol && (
                          <TableCell
                            className={cn(
                              "text-right",
                              !Number.isFinite(row.target) &&
                                "text-muted-foreground",
                            )}
                          >
                            {Number.isFinite(row.target)
                              ? formatValue(row.target)
                              : "n.a"}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TooltipProvider>
        </div>

        <PaginationControls
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={store.setPage}
          onPageSizeChange={store.setPageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          disabled={loading}
          labels={{
            pageLabel: ({ page: currentPage, totalPages }) =>
              formatMetricPageLabel({
                total,
                page: currentPage,
                totalPages,
                t,
              }),
          }}
        />
      </div>
    );
  },
);

export default DataTableGrid;
